import { forwardRef, memo, type Ref } from "react";
import { Pressable } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { VideoView, type VideoPlayer } from "expo-video";

/**
 * expo-video merges native (PureComponent) and web (forwardRef) VideoView
 * declarations, collapsing the ref type to `{}`. We re-declare the instance
 * methods that exist on both platforms so consumers can call them safely.
 */
export interface VideoViewRef {
  enterFullscreen(): Promise<void>;
  exitFullscreen(): Promise<void>;
  startPictureInPicture(): Promise<void>;
  stopPictureInPicture(): Promise<void>;
}

type VideoPlayerViewProps = {
  player: VideoPlayer;
  isFullscreen: boolean;
  isLandscape: boolean;
  onTap: () => void;
  onFullscreenEnter: () => void;
  onFullscreenExit: () => void;
  onFirstFrameRender: () => void;
};

const VideoPlayerViewInner = forwardRef<VideoViewRef, VideoPlayerViewProps>(
  ({ player, isFullscreen, isLandscape, onTap, onFullscreenEnter, onFullscreenExit, onFirstFrameRender }, ref) => {
    return (
      <Pressable onPress={onTap} style={styles.pressable}>
        <VideoView
          ref={ref as Ref<never>}
          // @ts-expect-error expo-video merges native + web VideoView declarations, creating an unsatisfiable intersection type for `player`
          player={player}
          style={styles.videoView}
          contentFit={isFullscreen ? "contain" : "cover"}
          nativeControls={isFullscreen || isLandscape}
          allowsFullscreen={true}
          allowsVideoFrameAnalysis={false}
          onFullscreenEnter={onFullscreenEnter}
          onFullscreenExit={onFullscreenExit}
          onFirstFrameRender={onFirstFrameRender}
        />
      </Pressable>
    );
  },
);

export const VideoPlayerView = memo(VideoPlayerViewInner);

const styles = StyleSheet.create((theme) => ({
  pressable: {
    borderRadius: theme.spacing.md,
    borderCurve: "continuous",
    overflow: "hidden",
    width: "100%",
    height: "100%",
  },
  videoView: {
    width: "100%",
    height: "100%",
  },
}));
